import React, { Fragment, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useParams, Link } from "react-router-dom";
import { toast } from "react-toastify";
import { Col, Row, Table } from "react-bootstrap";
import CancelButton from "Common/Components/Buttons/CancelButton";
import LoadingSpinner from "Common/Components/LoaderErrorWrapper/LoadingSpinner";
import FormattedDate from "Common/Components/NameWithDate.tsx/FormattedDate";
import PageBreadcrumb from "Common/Components/PageBreadcrumb/PageBreadcrumb";
import PageContainer from "Common/Components/PageContainer/PageContainer";
import { MecordColors } from "Constants/Color";
import { fetchGRNDetail } from "slices/Warehouse/ItemInward/ReturnedAccessories/thunk";

const AddStockViewReadOnly: React.FC<{ acc: any; onClose: () => void }> = ({ acc, onClose }) => {
  return (
    <>
      <style>{`
        .stock-table { font-family: "Poppins", sans-serif; font-size: 13px; }
        .stock-table thead th { background-color: ${MecordColors.LightGrayColor}; color: ${MecordColors.NormalText}; font-weight: 500; text-align: center; }
        .stock-table tbody tr:hover { background-color: ${MecordColors.LightGrayColorHover}; }
        .stock-table td, .stock-table th { vertical-align: middle; text-align: center; }
      `}</style>

      <div className="mb-4 mt-3" style={{ overflowX: "auto" }}>
        <Table className="table align-middle">
          <thead style={{ backgroundColor: MecordColors.LightGrayColor }}>
            <tr>
              <th style={{ minWidth: 60 }}>Sr. No</th>
              <th style={{ minWidth: 180 }}>Accessory</th>
              <th style={{ minWidth: 120 }}>Received Qty</th>
              <th style={{ minWidth: 120 }}>Good Qty</th>
              <th style={{ minWidth: 130 }}>Damaged Qty</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>1</td>
              <td>{acc.product_name}</td>
              <td>{acc.received_qty}</td>
              <td>{acc.good_qty}</td>
              <td>{acc.damaged_qty}</td>
            </tr>
          </tbody>
        </Table>
      </div>

      <hr />

      <div className="mb-4" style={{ overflowX: "auto" }}>
        <Table className="stock-table" bordered>
          <thead>
            <tr>
              <th style={{ minWidth: 60 }}>Sr No.</th>
              <th style={{ minWidth: 160 }}>Accessory</th>
              <th style={{ minWidth: 170 }}>Warranty Date</th>
              <th style={{ minWidth: 180 }}>Serial No.</th>
              <th style={{ minWidth: 100 }}>QC</th>
              <th style={{ minWidth: 180 }}>Remarks</th>
            </tr>
          </thead>
          <tbody>
            {(acc.grn_products || []).map((row: any, i: number) => (
              <tr key={i}>
                <td>{i + 1}</td>
                <td>{acc.product_name}</td>
                <td>
                  <FormattedDate date={row.warranty_date} />
                </td>
                <td>{row.serial_no || "-"}</td>
                <td>
                  <span className={row.qc === "QC Pass" ? "text-success fw-bold" : "text-danger fw-bold"}>
                    {row.qc}
                  </span>
                </td>
                <td>{row.remarks || "-"}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>

      <div className="d-flex flex-column flex-md-row justify-content-center gap-3 mt-3">
        <CancelButton showConfirmation={false} onClick={onClose} label="Back" />
      </div>
    </>
  );
};

const ViewReturnedAccessoriesGRN: React.FC = () => {
  document.title = "View GRN Details";

  const { inward_id } = useParams<{ inward_id: string }>();
  const dispatch = useDispatch<any>();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<any>(null);
  const [viewStockTarget, setViewStockTarget] = useState<any>(null);

  useEffect(() => {
    if (!inward_id) return;
    setLoading(true);
    dispatch(fetchGRNDetail(inward_id))
      .unwrap()
      .then((res: any) => setData(res?.data || res))
      .catch((err: any) => toast.error(err?.message || "Failed to fetch GRN details"))
      .finally(() => setLoading(false));
  }, [dispatch, inward_id]);

  const dv = (val: any) => (val !== null && val !== undefined && val !== "" ? val : "-");

  return (
    <Fragment>
      <PageContainer>
        <PageBreadcrumb
          title="View GRN Details"
          breadcrumbs={[
            { label: "Warehouse", to: "" },
            { label: "View GRN Details", to: "" },
          ]}
          backNavigationPath={-1}
        />
        <hr />

        {loading ? (
          <LoadingSpinner />
        ) : viewStockTarget ? (
          <AddStockViewReadOnly
            acc={viewStockTarget}
            onClose={() => setViewStockTarget(null)}
          />
        ) : (
          <>
            {/* GRN No & Date */}
            <Row className="mb-3 g-3">
              <h5 className="fw-semibold mb-1">GRN Details</h5>
              <Col lg={3}>
                <div className="mb-1">
                  <strong>GRN No.:</strong>
                  <span className="ms-1">{dv(data?.ragrn_no)}</span>
                </div>
              </Col>
              <Col lg={3}>
                <div className="mb-1">
                  <strong>GRN Date:</strong>
                  <span className="ms-1">
                    <FormattedDate date={data?.created_at} />
                  </span>
                </div>
              </Col>
              <Col lg={3}>
                <div className="mb-1">
                  <strong>Status:</strong>
                  <span className="ms-1">{dv(data?.status)}</span>
                </div>
              </Col>
            </Row>

            <hr className="my-2" />

            {/* Challan Details + Inward Details */}
            <Row className="mb-3 g-3">
              <Col lg={5} md={12}>
                <h5 className="fw-semibold mb-2">Challan Details</h5>
                <div
                  className="p-3 rounded border mb-2"
                  style={{ backgroundColor: MecordColors?.ActiveBackground }}
                >
                  <Row className="g-3">
                    <Col md={6}>
                      <p className="mb-2">
                        <strong>Challan No.:</strong>{" "}
                        {dv(data?.challan?.challan_no)}
                      </p>
                    </Col>
                    <Col md={6}>
                      <p className="mb-2">
                        <strong>Challan Date:</strong>{" "}
                        <FormattedDate date={data?.challan?.challan_date} />
                      </p>
                    </Col>
                    <Col md={6}>
                      <p className="mb-2">
                        <strong>Sales Order No.:</strong>{" "}
                        {dv(data?.challan?.sales_order_service?.sales_order_number)}
                      </p>
                    </Col>
                    <Col md={6}>
                      <p className="mb-2">
                        <strong>Warehouse:</strong>{" "}
                        {dv(data?.warehouse?.warehouse_name)}
                      </p>
                    </Col>
                  </Row>
                </div>
              </Col>

              <Col lg={7} md={12}>
                <h5 className="fw-semibold mb-2">Inward Details</h5>
                <div
                  className="p-3 rounded border mb-2"
                  style={{ backgroundColor: MecordColors?.ActiveBackground }}
                >
                  <Row className="g-3">
                    <Col md={6}>
                      <p className="mb-2">
                        <strong>Received Date:</strong>{" "}
                        <FormattedDate date={data?.received_date} />
                      </p>
                    </Col>
                    <Col md={6}>
                      <p className="mb-2">
                        <strong>Received By:</strong>{" "}
                        {dv(data?.received_by)}
                      </p>
                    </Col>
                    {/* <Col md={6}>
                      <p className="mb-2">
                        <strong>Created By:</strong>{" "}
                        {dv(
                          data?.created_by_user?.name ||
                            data?.created_by_user?.email,
                        )}
                      </p>
                    </Col> */}
                  </Row>
                </div>
              </Col>
            </Row>

            <hr />

            {/* Products Table */}
            <h5 className="fw-semibold mb-2">Accessories</h5>
            <div className="mb-4 mt-3" style={{ overflowX: "auto" }}>
              <Table className="table align-middle">
                <thead style={{ backgroundColor: MecordColors.LightGrayColor }}>
                  <tr>
                    <th style={{ minWidth: 60 }}>Sr. No</th>
                    <th style={{ minWidth: 180 }}>Accessory</th>
                    <th style={{ minWidth: 150 }}>SKU</th>
                    <th style={{ minWidth: 120, textAlign: "center" }}>Received Qty</th>
                    <th style={{ minWidth: 120, textAlign: "center" }}>Good Qty</th>
                    <th style={{ minWidth: 130, textAlign: "center" }}>Damaged Qty</th>
                    <th style={{ minWidth: 160 }}>Service</th>
                    <th style={{ minWidth: 130, textAlign: "center", position: "sticky", right: 0, background: MecordColors.LightGrayColor, zIndex: 2 }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    const rows: React.ReactNode[] = [];
                    let sr = 0;
                    const items: any[] = Array.isArray(data?.items) ? data.items : [];
                    items.forEach((item: any) => {
                      const accs: any[] = Array.isArray(item.accessories)
                        ? item.accessories
                        : [];
                      accs.forEach((acc: any) => {
                        sr += 1;
                        rows.push(
                          <tr key={`${item.sales_order_service_item_id}-${sr}`}>
                            <td>{sr}</td>
                            <td>{dv(acc.product_name)}</td>
                            <td>{dv(acc.product_sku)}</td>
                            <td style={{ textAlign: "center" }}>{dv(acc.received_qty)}</td>
                            <td style={{ textAlign: "center" }}>{dv(acc.good_qty)}</td>
                            <td style={{ textAlign: "center" }}>{dv(acc.damaged_qty)}</td>
                            <td>{dv(item.product_name)}</td>
                            <td style={{ textAlign: "center", position: "sticky", right: 0, background: "white", zIndex: 2 }}>
                              <button
                                className="btn btn-sm"
                                style={{ backgroundColor: MecordColors?.ThemeBGColor, color: "#FFF" }}
                                onClick={() => setViewStockTarget(acc)}
                              >
                                View Stock
                              </button>
                            </td>
                          </tr>,
                        );
                      });
                    });
                    if (rows.length === 0) {
                      return (
                        <tr>
                          <td colSpan={7} className="text-center text-muted">
                            No accessories found
                          </td>
                        </tr>
                      );
                    }
                    return rows;
                  })()}
                </tbody>
              </Table>
            </div>

            {/* Notes */}
            <Row className="mb-3 g-3">
              <Col md={12}>
                <div className="card shadow-sm border">
                  <div
                    className="p-2 rounded-top-2"
                    style={{
                      backgroundColor: MecordColors?.LightGrayColor,
                      color: MecordColors?.NormalText,
                    }}
                  >
                    <h5 className="card-title mb-0 fw-semibold">
                      <i className="mdi mdi-note-text-outline me-2"></i>
                      Notes
                    </h5>
                  </div>
                  <div className="card-body">
                    <div className="p-2">
                      <p style={{ whiteSpace: "pre-wrap" }} className="mb-0 text-dark">
                        {data?.note || (
                          <span className="text-muted fst-italic">
                            No notes available
                          </span>
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              </Col>
            </Row>

            {/* Attachments */}
            <Row className="mb-3 g-3">
              <Col md={12}>
                <div className="card shadow-sm border">
                  <div
                    className="p-2 rounded-top-2"
                    style={{
                      backgroundColor: MecordColors?.LightGrayColor,
                      color: MecordColors?.NormalText,
                    }}
                  >
                    <h5 className="card-title mb-0 fw-semibold">
                      <i className="mdi mdi-attachment me-2"></i>
                      Attachments
                    </h5>
                  </div>
                  <div className="card-body">
                    {data?.attachments &&
                    Array.isArray(data.attachments) &&
                    data.attachments.length > 0 ? (
                      <div className="row g-3">
                        {data.attachments.map(
                          (attachment: string, index: number) => (
                            <div key={index} className="col-md-6 col-lg-4">
                              <div className="border rounded p-3 bg-light">
                                <div className="d-flex align-items-center">
                                  <i className="mdi mdi-file-document-outline me-2 text-primary fs-4"></i>
                                  <div className="flex-grow-1">
                                    <p className="mb-1 fw-medium text-truncate">
                                      Attachment {index + 1}
                                    </p>
                                    <small className="text-muted">
                                      Document
                                    </small>
                                  </div>
                                  <Link
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    to={attachment}
                                    className="btn btn-sm ms-2"
                                    style={{
                                      backgroundColor:
                                        MecordColors?.PrimaryButtonBGColor,
                                      color: MecordColors?.PrimaryButtonTextColor,
                                    }}
                                  >
                                    <i className="mdi mdi-download me-1"></i>
                                    View
                                  </Link>
                                </div>
                              </div>
                            </div>
                          )
                        )}
                      </div>
                    ) : (
                      <div className="p-2 text-center">
                        <span className="text-muted fst-italic">
                          No attachments available
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </Col>
            </Row>

            {/* Actions */}
            <div className="d-flex flex-column flex-md-row justify-content-center gap-3 mt-3">
              <CancelButton showConfirmation={false} redirectTo="/warehouse-item-inward" />
            </div>
          </>
        )}
      </PageContainer>
    </Fragment>
  );
};

export default ViewReturnedAccessoriesGRN;
